import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, ListGroup } from 'react-bootstrap';
import { FaExpand, FaSave, FaTimes } from 'react-icons/fa';
import WorkflowAutocomplete from './WorkflowAutocomplete';
import { WORKFLOW_FUNCTIONS } from './workflowFunctions';

const RUNBOOK_VARIABLES = [
  { name: 'RB_CURRENTRECORDNUMBER', description: 'Returns the current record number being processed within a ForEach loop (1-based)', category: 'Runbook' },
  { name: 'RB_TOTALNUMBEROFRECORDS', description: 'Returns the total number of records being processed by the parent ForEach step', category: 'Runbook' },
  { name: 'RB_RECORDCOUNT', description: 'Returns the number of records processed by a specific step within the runbook', category: 'Runbook' }
];

function AdvancedExpressionEditor({
  show,
  onHide,
  value,
  onChange,
  label,
  allNodes,
  currentNode,
  edges,
  runbookVariables,
  readOnly
}) {
  const [localValue, setLocalValue] = useState(value || '');
  const [searchFilter, setSearchFilter] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({ String: true, Date: true, Math: true, Conversion: true, Logical: true, Lookup: true });
  const [cursorPosition, setCursorPosition] = useState(0);
  const [activeLeftTab, setActiveLeftTab] = useState('functions');
  const [expandedSteps, setExpandedSteps] = useState({});
  const textareaRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  useEffect(() => {
    if (show && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus();
        const length = localValue.length;
        textareaRef.current?.setSelectionRange(length, length);
        setCursorPosition(length);
      }, 100);
    }
  }, [show]);

  const handleSave = () => {
    onChange({ target: { value: localValue } });
    onHide();
  };

  const handleCancel = () => {
    setLocalValue(value || '');
    onHide();
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const resolveStepToDataSource = (stepId, edgesArg) => {
    if (!allNodes) return null;
    const step = allNodes.find(n => (n.data?.stepId || n.id) === stepId);
    if (!step) return null;
    const stepType = step.data?.stepType;
    if (stepType === 'forEach') {
      let dataStepId = step.data?.dataStepId;
      if (!dataStepId && edgesArg) {
        const incomingEdge = edgesArg.find(e => e.target === (step.data?.stepId || step.id));
        if (incomingEdge) {
          const sourceNode = allNodes.find(n => (n.data?.stepId || n.id) === incomingEdge.source);
          if (sourceNode && (sourceNode.data?.stepType === 'query' || sourceNode.data?.stepType === 'queryTableCache')) {
            dataStepId = sourceNode.data?.stepId || sourceNode.id;
          }
        }
      }
      if (dataStepId) return resolveStepToDataSource(dataStepId, edgesArg);
      return null;
    }
    if (stepType === 'lookupTableCache') {
      const dataStepId = step.data?.dataStepId;
      if (dataStepId) return resolveStepToDataSource(dataStepId, edgesArg);
      return null;
    }
    return step;
  };

  const getFieldsFromStep = (stepId) => {
    if (!allNodes) return [];
    const referenceableStepTypes = ['forEach', 'declareVar', 'lookup', 'create', 'update', 'lookupTableCache'];
    const originalStep = allNodes.find(n => (n.data?.stepId || n.id) === stepId);
    if (!originalStep) return [];
    const originalStepType = originalStep.data?.stepType;
    if (!referenceableStepTypes.includes(originalStepType)) return [];
    const dataSource = resolveStepToDataSource(stepId, edges);
    if (!dataSource) return [];
    const fieldsRaw = dataSource.data?.fieldsToQuery;
    if (!fieldsRaw) return [];
    return fieldsRaw.split(',').map(f => f.trim()).filter(Boolean);
  };

  const getAvailableSteps = () => {
    if (!allNodes || !currentNode) return [];
    const referenceableStepTypes = ['forEach', 'declareVar', 'lookup', 'create', 'update', 'lookupTableCache'];
    return allNodes
      .filter(n => {
        const stepType = n.data?.stepType;
        const stepId = n.data?.stepId || n.id;
        if (stepType === 'start' || stepType === 'groupNode') return false;
        if (stepId === (currentNode.data?.stepId || currentNode.id)) return false;
        if (!referenceableStepTypes.includes(stepType)) return false;
        if (n.data?.stepOrder >= currentNode.data?.stepOrder) return false;
        return true;
      })
      .map(n => ({
        id: n.data?.stepId || n.id,
        label: n.data?.label || n.data?.stepName || n.data?.stepId || n.id,
        stepType: n.data?.stepType,
        description: n.data?.description || '',
        displayText: n.data?.label || n.data?.stepName || n.data?.stepId || n.id
      }));
  };

  const insertFunction = (func) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = localValue.substring(0, cursorPos);
    const textAfter = localValue.substring(cursorPos);

    const newValue = textBefore + func.name + '()' + textAfter;
    setLocalValue(newValue);

    setTimeout(() => {
      const newCursorPos = cursorPos + func.name.length + 1;
      textarea.selectionStart = newCursorPos;
      textarea.selectionEnd = newCursorPos;
      textarea.focus();
    }, 0);
  };

  const insertField = (stepId, fieldName) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    const textBefore = localValue.substring(0, cursorPos);
    const textAfter = localValue.substring(cursorPos);
    const newValue = textBefore + '{{' + stepId + '_' + fieldName + '}}' + textAfter;
    setLocalValue(newValue);
    setTimeout(() => {
      const newCursorPos = cursorPos + 2 + stepId.length + 1 + fieldName.length + 2;
      textarea.selectionStart = newCursorPos;
      textarea.selectionEnd = newCursorPos;
      textarea.focus();
    }, 0);
  };

  const insertRunbookVariable = (varName) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    const textBefore = localValue.substring(0, cursorPos);
    const textAfter = localValue.substring(cursorPos);
    const newValue = textBefore + '{{' + varName + '}}' + textAfter;
    setLocalValue(newValue);
    setTimeout(() => {
      const newCursorPos = cursorPos + 2 + varName.length + 2;
      textarea.selectionStart = newCursorPos;
      textarea.selectionEnd = newCursorPos;
      textarea.focus();
    }, 0);
  };

  const toggleStep = (stepId) => {
    setExpandedSteps(prev => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  const filteredFunctions = WORKFLOW_FUNCTIONS.filter(func =>
    func.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
    func.description.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const groupedFunctions = filteredFunctions.reduce((groups, func) => {
    const category = func.category || 'Other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(func);
    return groups;
  }, {});

  const findMatchingBracket = (text, position) => {
    const char = text[position];
    const pairs = { '(': ')', ')': '(', '[': ']', ']': '[', '{': '}', '}': '{' };

    if (!pairs[char]) return null;

    const isOpening = ['(', '[', '{'].includes(char);
    const searchChar = char;
    const matchChar = pairs[char];
    const direction = isOpening ? 1 : -1;
    const start = position + direction;
    const end = isOpening ? text.length : -1;

    let depth = 1;
    for (let i = start; isOpening ? i < end : i > end; i += direction) {
      if (text[i] === searchChar) depth++;
      if (text[i] === matchChar) depth--;
      if (depth === 0) return i;
    }

    return null;
  };

  const highlightSyntax = (text) => {
    if (!text) return [];

    let matchingPositions = new Set();
    if (cursorPosition > 0 && cursorPosition <= text.length) {
      const beforePos = cursorPosition - 1;
      const matchBefore = findMatchingBracket(text, beforePos);
      if (matchBefore !== null) {
        matchingPositions.add(beforePos);
        matchingPositions.add(matchBefore);
      }
    }
    if (cursorPosition >= 0 && cursorPosition < text.length) {
      const matchAt = findMatchingBracket(text, cursorPosition);
      if (matchAt !== null) {
        matchingPositions.add(cursorPosition);
        matchingPositions.add(matchAt);
      }
    }

    const parts = [];
    let currentIndex = 0;

    const regex = /(FX_[A-Z]+)|({{[^}]+}})|(\"[^\"]*\")|(\d+\.?\d*)|([(),|[\]{}])/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > currentIndex) {
        for (let i = currentIndex; i < match.index; i++) {
          parts.push({
            type: matchingPositions.has(i) ? 'matched-bracket' : 'text',
            value: text[i]
          });
        }
      }

      if (match[1]) {
        parts.push({ type: 'function', value: match[1] });
      } else if (match[2]) {
        parts.push({ type: 'reference', value: match[2] });
      } else if (match[3]) {
        parts.push({ type: 'string', value: match[3] });
      } else if (match[4]) {
        parts.push({ type: 'number', value: match[4] });
      } else if (match[5]) {
        const isMatched = matchingPositions.has(match.index);
        parts.push({
          type: isMatched ? 'matched-bracket' : 'operator',
          value: match[5]
        });
      }

      currentIndex = match.index + match[0].length;
    }

    if (currentIndex < text.length) {
      for (let i = currentIndex; i < text.length; i++) {
        parts.push({
          type: matchingPositions.has(i) ? 'matched-bracket' : 'text',
          value: text[i]
        });
      }
    }

    return parts;
  };

  const syntaxParts = highlightSyntax(localValue);

  return (
    <Modal show={show} onHide={handleCancel} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaExpand className="me-2" />
          {readOnly ? `View Expression - ${label}` : `Advanced Expression Editor - ${label}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={4}>
            <div className="d-flex mb-2" style={{ borderBottom: '2px solid #dee2e6' }}>
              <button
                className="btn btn-link btn-sm px-3 py-1 text-decoration-none"
                style={{
                  borderBottom: activeLeftTab === 'functions' ? '2px solid #0d6efd' : '2px solid transparent',
                  marginBottom: '-2px',
                  color: activeLeftTab === 'functions' ? '#0d6efd' : '#6c757d',
                  fontWeight: activeLeftTab === 'functions' ? 'bold' : 'normal'
                }}
                onClick={() => setActiveLeftTab('functions')}
              >
                Functions
              </button>
              <button
                className="btn btn-link btn-sm px-3 py-1 text-decoration-none"
                style={{
                  borderBottom: activeLeftTab === 'fields' ? '2px solid #0d6efd' : '2px solid transparent',
                  marginBottom: '-2px',
                  color: activeLeftTab === 'fields' ? '#0d6efd' : '#6c757d',
                  fontWeight: activeLeftTab === 'fields' ? 'bold' : 'normal'
                }}
                onClick={() => setActiveLeftTab('fields')}
              >
                Fields
              </button>
            </div>

            {activeLeftTab === 'functions' && (
              <>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Search functions..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="mb-2"
                />
                <div style={{ maxHeight: '460px', overflowY: 'auto' }}>
                  {Object.entries(groupedFunctions).map(([category, functions]) => (
                    <div key={category} className="mb-2">
                      <div
                        onClick={() => toggleCategory(category)}
                        style={{
                          backgroundColor: '#f8f9fa',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: '0.9rem',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          userSelect: 'none'
                        }}
                      >
                        <span>{category} Functions ({functions.length})</span>
                        <span>{expandedCategories[category] ? '▼' : '▶'}</span>
                      </div>

                      {expandedCategories[category] && (
                        <ListGroup style={{ fontSize: '0.85rem', marginTop: '4px' }}>
                          {functions.map((func, index) => (
                            <ListGroup.Item
                              key={index}
                              action={!readOnly}
                              onClick={readOnly ? undefined : () => insertFunction(func)}
                              style={{ cursor: readOnly ? 'default' : 'pointer', padding: '8px 12px' }}
                            >
                              <div style={{ fontWeight: 'bold', color: '#0d6efd', fontSize: '0.85rem' }}>
                                {func.name}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '2px' }}>
                                {func.description}
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#adb5bd', marginTop: '4px', fontFamily: 'monospace' }}>
                                {func.signature}
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeLeftTab === 'fields' && (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {getAvailableSteps().length === 0 ? (
                  <div className="text-muted small p-2">No referenceable steps available before this step.</div>
                ) : (
                  getAvailableSteps().map(step => {
                    const fields = getFieldsFromStep(step.id);
                    const isExpanded = !!expandedSteps[step.id];
                    return (
                      <div key={step.id} className="mb-1">
                        <div
                          onClick={() => toggleStep(step.id)}
                          style={{
                            backgroundColor: '#f8f9fa',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            userSelect: 'none'
                          }}
                        >
                          <span title={step.displayText} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '85%' }}>
                            {step.label || step.id}
                            <span style={{ fontWeight: 'normal', color: '#6c757d', fontSize: '0.75rem', marginLeft: '6px' }}>
                              {step.id}
                            </span>
                          </span>
                          <span style={{ flexShrink: 0 }}>{isExpanded ? '▼' : '▶'}</span>
                        </div>
                        {isExpanded && (
                          <ListGroup style={{ fontSize: '0.82rem', marginTop: '2px', marginLeft: '8px' }}>
                            {fields.length === 0 ? (
                              <ListGroup.Item style={{ padding: '6px 12px', color: '#6c757d', fontStyle: 'italic', fontSize: '0.8rem' }}>
                                No fields available
                              </ListGroup.Item>
                            ) : (
                              fields.map((field, fi) => (
                                <ListGroup.Item
                                  key={fi}
                                  action={!readOnly}
                                  onClick={readOnly ? undefined : () => insertField(step.id, field)}
                                  style={{ cursor: readOnly ? 'default' : 'pointer', padding: '6px 12px' }}
                                >
                                  <div style={{ color: '#1971c2', fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: '500' }}>
                                    {field}
                                  </div>
                                  <div style={{ fontSize: '0.7rem', color: '#868e96', marginTop: '1px', fontFamily: 'monospace' }}>
                                    {'{{'}{step.id}_{field}{'}}'}
                                  </div>
                                </ListGroup.Item>
                              ))
                            )}
                          </ListGroup>
                        )}
                      </div>
                    );
                  })
                )}

                <div className="mt-3">
                  <div
                    style={{
                      backgroundColor: '#f0f4ff',
                      padding: '8px 12px',
                      fontWeight: 'bold',
                      fontSize: '0.85rem',
                      borderRadius: '4px',
                      color: '#495057'
                    }}
                  >
                    Runbook Variables
                  </div>
                  <ListGroup style={{ fontSize: '0.82rem', marginTop: '2px' }}>
                    {RUNBOOK_VARIABLES.map((rv, ri) => (
                      <ListGroup.Item
                        key={ri}
                        action={!readOnly}
                        onClick={readOnly ? undefined : () => insertRunbookVariable(rv.name)}
                        style={{ cursor: readOnly ? 'default' : 'pointer', padding: '6px 12px' }}
                      >
                        <div style={{ color: '#1971c2', fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: '500' }}>
                          {rv.name}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#868e96', marginTop: '1px' }}>
                          {rv.description}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              </div>
            )}
          </Col>

          {/* Right side - Editor */}
          <Col md={8}>
            {/* Syntax Highlighted Preview */}
            <div className="mb-3">
              <Form.Label className="small fw-bold">Preview with Syntax Highlighting</Form.Label>
              <div
                style={{
                  backgroundColor: '#1e1e1e',
                  color: '#d4d4d4',
                  padding: '12px',
                  borderRadius: '4px',
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  fontSize: '0.85rem',
                  lineHeight: '1.6',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {syntaxParts.map((part, index) => {
                  const colors = {
                    function: '#dcdcaa',
                    reference: '#9cdcfe',
                    string: '#ce9178',
                    number: '#b5cea8',
                    operator: '#d4d4d4',
                    'matched-bracket': '#00ff00',
                    text: '#d4d4d4'
                  };

                  const isMatchedBracket = part.type === 'matched-bracket';

                  return (
                    <span
                      key={index}
                      style={{
                        color: colors[part.type] || colors.text,
                        backgroundColor: isMatchedBracket ? 'rgba(0, 255, 0, 0.2)' : 'transparent',
                        fontWeight: isMatchedBracket ? 'bold' : 'normal',
                        borderRadius: isMatchedBracket ? '2px' : '0',
                        padding: isMatchedBracket ? '0 2px' : '0'
                      }}
                    >
                      {part.value}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Editable textarea with autocomplete */}
            <Form.Label className="small fw-bold">Expression</Form.Label>
            {readOnly ? (
              <Form.Control
                as="textarea"
                value={localValue}
                readOnly
                rows={15}
                style={{
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  fontSize: '0.9rem',
                  minHeight: '320px',
                  resize: 'vertical',
                  backgroundColor: '#f8f9fa'
                }}
              />
            ) : (
              <WorkflowAutocomplete
                ref={textareaRef}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onClick={(e) => setCursorPosition(e.target.selectionStart)}
                onKeyUp={(e) => setCursorPosition(e.target.selectionStart)}
                allNodes={allNodes}
                currentNode={currentNode}
                edges={edges}
                runbookVariables={runbookVariables}
                placeholder="Type FX_ for functions, {{ for step references"
                className="form-control"
                style={{
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  fontSize: '0.9rem',
                  minHeight: '320px',
                  resize: 'vertical'
                }}
                as="textarea"
                rows={15}
              />
            )}

            {!readOnly && (
              <Form.Text className="text-muted d-block mt-2">
                Type <strong>FX_</strong> to see available functions, <strong>{'{{'}</strong> to reference steps and fields.
                Click a function on the left to insert it at the cursor position.
              </Form.Text>
            )}

            <div className="text-muted small mt-2">
              Character count: {localValue.length}
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        {readOnly ? (
          <Button variant="secondary" onClick={handleCancel}>
            <FaTimes className="me-1" /> Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleCancel}>
              <FaTimes className="me-1" /> Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              <FaSave className="me-1" /> Save
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default AdvancedExpressionEditor;
